import React from 'react';
import { Check, Ruler } from 'lucide-react';
import { isLightHex, rupees } from '@/lib/shop';

const priceOf = (v) => v?.featuredOffer?.price ?? v?.price;

const VariantPicker = ({ theme = [], variants = [], selected, optionStates, onSelect, onSizeChart, showPrices }) => {
  const byId = new Map(variants.map((v) => [v.id, v]));
  const currentPrice = priceOf(selected);

  return (
    <div className="space-y-5">
      {theme.map((axis) => {
        const states = optionStates?.[axis.key] || [];
        if (!states.length) return null;
        const meta = new Map((axis.options || []).map((o) => [o.value, o]));
        const chosen = meta.get(selected?.options?.[axis.key]);
        const isColor = axis.type === 'color' || axis.key === 'color';
        const target = (s) => byId.get(s.variantId);
        // Image swatches only when each colour really has its own picture.
        const useImages = isColor && states.every((s) => target(s)?.swatchImage);
        const selectedState = states.find((s) => s.state === 'selected');

        return (
          <fieldset key={axis.key}>
            <legend className="flex w-full items-center justify-between gap-3">
              <span className="text-sm text-ink-500">
                {axis.label}:{' '}
                <span className="font-semibold text-ink-900">{chosen?.label || selected?.options?.[axis.key]}</span>
                {selectedState && !selected?.inStock && <span className="ml-2 font-semibold text-danger">Out of stock</span>}
              </span>
              {onSizeChart && /size|waist/.test(axis.key) && (
                <button type="button" onClick={onSizeChart} className="lk-focus inline-flex items-center gap-1 rounded text-sm font-semibold text-brand-secondary">
                  <Ruler className="h-4 w-4" />
                  Size chart
                </button>
              )}
            </legend>

            <div className={`mt-2.5 flex flex-wrap ${useImages ? 'gap-2.5' : isColor ? 'gap-3' : 'gap-2'}`} role="radiogroup" aria-label={axis.label}>
              {states.map((s) => {
                const o = meta.get(s.value) || { label: s.value, value: s.value };
                const v = target(s);
                const isSel = s.state === 'selected';
                const oos = s.state === 'out_of_stock';
                const unavailable = s.state === 'unavailable';
                const price = priceOf(v);
                const priceHint = showPrices && !unavailable && price != null && price !== currentPrice && !isSel ? rupees(price) : null;
                const hint = unavailable ? `${o.label} — available in another combination` : oos ? `${o.label} — out of stock` : o.label;
                const pick = () => s.variantId && onSelect(s.variantId, unavailable);

                if (useImages) {
                  return (
                    <button
                      key={s.value}
                      type="button"
                      role="radio"
                      aria-checked={isSel}
                      title={hint}
                      aria-label={hint}
                      onClick={pick}
                      className={`lk-focus group relative flex w-[68px] flex-col items-center gap-1 rounded-xl p-1 transition ${
                        isSel ? 'ring-2 ring-ink-900' : 'ring-1 ring-ink-200 hover:ring-ink-400'
                      } ${unavailable ? 'opacity-45' : ''}`}
                    >
                      <span className="relative block aspect-[4/5] w-full overflow-hidden rounded-lg bg-surface-sunken">
                        <img src={v.swatchImage} alt="" className={`h-full w-full object-cover ${oos ? 'opacity-50' : ''}`} loading="lazy" />
                        {oos && <span className="absolute inset-0 bg-[linear-gradient(to_top_right,transparent_calc(50%-1px),#d93025,transparent_calc(50%+1px))]" />}
                      </span>
                      <span className={`w-full truncate text-center text-[11px] leading-tight ${isSel ? 'font-semibold text-ink-900' : 'text-ink-700'}`}>{o.label}</span>
                      {priceHint && <span className="tabular text-[10px] font-medium text-ink-500">{priceHint}</span>}
                    </button>
                  );
                }

                if (isColor) {
                  const hex = v?.swatchHex || o.hex;
                  return (
                    <button
                      key={s.value}
                      type="button"
                      role="radio"
                      aria-checked={isSel}
                      title={hint}
                      aria-label={hint}
                      onClick={pick}
                      className={`lk-focus flex w-14 flex-col items-center gap-1 rounded-lg ${unavailable ? 'opacity-40' : ''}`}
                    >
                      <span
                        className={`relative flex h-10 w-10 items-center justify-center rounded-full transition ${
                          isSel ? 'ring-2 ring-ink-900 ring-offset-2' : isLightHex(hex) ? 'ring-1 ring-ink-200' : 'hover:ring-1 hover:ring-ink-400 hover:ring-offset-2'
                        }`}
                        style={{ background: s.value === 'multicolor' ? 'conic-gradient(#ef4444,#f59e0b,#22c55e,#3b82f6,#a855f7,#ef4444)' : hex || '#ccc' }}
                      >
                        {isSel && <Check className={`h-4 w-4 ${isLightHex(hex) ? 'text-ink-900' : 'text-white'}`} strokeWidth={3} />}
                        {oos && <span className="absolute inset-0 rounded-full bg-[linear-gradient(to_top_right,transparent_calc(50%-1px),#d93025,transparent_calc(50%+1px))]" />}
                      </span>
                      <span className={`w-full truncate text-center text-[11px] leading-tight ${isSel ? 'font-semibold text-ink-900' : 'text-ink-700'}`}>{o.label}</span>
                      {priceHint && <span className="tabular text-[10px] font-medium text-ink-500">{priceHint}</span>}
                    </button>
                  );
                }

                return (
                  <button
                    key={s.value}
                    type="button"
                    role="radio"
                    aria-checked={isSel}
                    title={hint}
                    aria-label={hint}
                    onClick={pick}
                    className={`lk-focus relative flex min-h-[44px] min-w-[52px] flex-col items-center justify-center rounded-xl border px-3.5 py-1.5 text-sm transition ${
                      isSel
                        ? 'border-ink-900 bg-ink-900 font-semibold text-white'
                        : unavailable
                          ? 'border-dashed border-ink-200 bg-white text-ink-400 hover:border-ink-400'
                          : 'border-ink-200 bg-white text-ink-900 hover:border-ink-900'
                    }`}
                  >
                    <span className={oos ? 'line-through decoration-danger decoration-2' : ''}>{o.label}</span>
                    {priceHint && <span className={`tabular text-[11px] ${isSel ? 'text-white/80' : 'text-ink-500'}`}>{priceHint}</span>}
                    {oos && !isSel && <span className="text-[10px] font-medium text-danger">Sold out</span>}
                  </button>
                );
              })}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
};

export default VariantPicker;
